import { useState, useRef, useCallback } from "react";

// ── useAskYourData hook ───────────────────────────────────────────────────────
// Manages conversation state and SSE streaming from POST /api/surveys/:id/ask

const API_BASE = process.env.REACT_APP_API_URL || "";

function getAuthHeader() {
  const token = localStorage.getItem("cp_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useAskYourData(surveyId) {
  const [messages,         setMessages]         = useState([]);
  const [streaming,        setStreaming]         = useState(false);
  const [streamingText,    setStreamingText]     = useState("");
  const [conversationId,   setConversationId]    = useState(null);
  const [error,            setError]             = useState("");
  const [suggestedChips,   setSuggestedChips]    = useState([]);
  const abortRef = useRef(null);

  const ask = useCallback(async (question, mode = "chat") => {
    if (streaming) return;
    setError("");
    setStreaming(true);
    setStreamingText("");
    setSuggestedChips([]);

    // Append user message immediately
    setMessages(prev => [...prev, { role: "user", content: question, timestamp: new Date().toISOString() }]);

    try {
      const resp = await fetch(`${API_BASE}/api/surveys/${surveyId}/ask`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body:    JSON.stringify({ question, conversationId, mode }),
        signal:  (abortRef.current = new AbortController()).signal,
      });

      if (!resp.ok || !resp.body) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${resp.status}`);
      }

      const reader  = resp.body.getReader();
      const decoder = new TextDecoder();
      let   buffer  = "";
      let   tokens  = "";
      let   finalPayload = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            const evt = JSON.parse(raw);
            if (evt.type === "token") {
              tokens += evt.content;
              setStreamingText(tokens);
            } else if (evt.type === "done") {
              finalPayload = evt.payload;
            }
            // "meta" events (stage changes) are silently consumed — could wire to a status indicator
          } catch { /* skip */ }
        }
      }

      if (finalPayload?.error) throw new Error(finalPayload.error);

      const answer       = finalPayload?.answer       || tokens;
      const sources      = finalPayload?.sources      || [];
      const followups    = finalPayload?.suggestedFollowups || [];
      const newConvId    = finalPayload?.conversationId;
      const meta         = finalPayload?.meta || {};

      if (newConvId) setConversationId(newConvId);
      setSuggestedChips(followups);

      setMessages(prev => [
        ...prev,
        { role: "assistant", content: answer, sources, meta, timestamp: new Date().toISOString() },
      ]);

    } catch (err) {
      if (err.name !== "AbortError") {
        setError(err.message || "Something went wrong.");
        setMessages(prev => [...prev, { role: "assistant", content: null, error: err.message, timestamp: new Date().toISOString() }]);
      }
    } finally {
      setStreaming(false);
      setStreamingText("");
    }
  }, [surveyId, conversationId, streaming]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setConversationId(null);
    setError("");
    setSuggestedChips([]);
    setStreamingText("");
    setStreaming(false);
  }, []);

  const loadConversation = useCallback(async (convId) => {
    if (streaming) return;
    setError("");
    try {
      const resp = await fetch(`${API_BASE}/api/surveys/${surveyId}/conversations/${convId}`, {
        headers: getAuthHeader(),
      });
      if (!resp.ok) throw new Error("Failed to load conversation");
      const data = await resp.json();
      const conv = data.conversation;
      setConversationId(conv.id);
      setMessages(
        (conv.messages || []).map(m => ({
          role: m.role, content: m.content,
          sources: m.sources || [], meta: m.meta || {},
          timestamp: m.timestamp,
        }))
      );
      setSuggestedChips([]);
    } catch (err) {
      setError(err.message);
    }
  }, [surveyId, streaming]);

  return { messages, streaming, streamingText, error, suggestedChips, ask, reset, loadConversation, conversationId };
}
